import { computed } from 'vue';

/**
 * Encuentra el material del elemento cuya categoría valida el ancho, y expone sus
 * dimensiones.
 *
 * Es la lógica de los productos planos: el suaje/imposición sólo tiene sentido cuando hay un
 * material que define el ancho útil, y las dimensiones de ese material son las del item que
 * entra en máquina. Estaba escrita dentro de `price_cajas.vue`; se extrajo acá para que
 * Offset use exactamente la misma y no dos copias que se separen con el tiempo (COTI-502).
 *
 * La categoría puede marcar `ValidaAnchoMaterial` en tres lugares distintos —la fila, la
 * categoría del material, o la relación proceso/elemento-tipo/categoría—, así que se
 * contemplan los tres.
 *
 * @param {import('vue').Ref<object>} elemento Modelo del elemento (localModel).
 */
export function useMaterialValidaAncho(elemento) {
  const categoriaValidaAncho = (categoria, proceso) =>
    categoria?.ValidaAnchoMaterial === true ||
    categoria?.MaterialCategoria?.ValidaAnchoMaterial === true ||
    (proceso?.Proceso?.ProcesoElementoTipoCategoriaMaterial || []).some(
      (rel) =>
        (rel?.CodigoCategoriaMaterial === categoria?.Codigo ||
          rel?.CategoriaMaterial?.Codigo === categoria?.Codigo) &&
        rel?.CategoriaMaterial?.ValidaAnchoMaterial === true,
    );

  /** Primer material cargado en una categoría que valide el ancho. */
  const materialSeleccionado = computed(() => {
    const secciones = elemento.value?.Secciones || [];

    for (const seccion of secciones) {
      for (const proceso of seccion.ElementoProceso || []) {
        for (const categoria of proceso.CategoriasMaterial || []) {
          if (!categoriaValidaAncho(categoria, proceso)) continue;

          const materiales = categoria.ElementoMateriales || [];
          if (materiales.length > 0) return materiales[0];
        }
      }
    }
    return null;
  });

  /** Hay una categoría que valida ancho, aunque todavía no tenga material cargado. */
  const hayMaterialQueValidaAncho = computed(() => {
    const secciones = elemento.value?.Secciones || [];

    for (const seccion of secciones) {
      for (const proceso of seccion.ElementoProceso || []) {
        for (const categoria of proceso.CategoriasMaterial || []) {
          if (categoriaValidaAncho(categoria, proceso)) return true;
        }
      }
    }
    return false;
  });

  const materialRef = computed(() => materialSeleccionado.value || null);

  /** Primer valor mayor a cero: la fila del elemento pisa lo que declare el material. */
  const primeraDimension = (...valores) => {
    for (const valor of valores) {
      const numero = Number(valor);
      if (numero > 0) return numero;
    }
    return 0;
  };

  const anchoMaterial = computed(() => {
    void elemento.value?.Secciones;
    const material = materialSeleccionado.value;
    return primeraDimension(material?.Ancho, material?.Material?.Ancho);
  });

  /**
   * La segunda dimensión no siempre se llama igual: cada MaterialCategoria declara cuáles
   * usa, y la de "Sustratos offset" marca Ancho y **Alto**, con Largo en cero — sus
   * materiales tienen `Largo: null`. Por eso se toma Largo y, si no hay, Alto: leer sólo
   * Largo dejaba el largo del item en 0 para todo sustrato de offset.
   */
  const largoMaterial = computed(() => {
    void elemento.value?.Secciones;
    const material = materialSeleccionado.value;
    return primeraDimension(
      material?.Largo,
      material?.Material?.Largo,
      material?.Alto,
      material?.Material?.Alto,
    );
  });

  return {
    materialSeleccionado,
    materialRef,
    anchoMaterial,
    largoMaterial,
    hayMaterialQueValidaAncho,
  };
}
